/**
 * Recommendations Page
 * Displays cost optimization recommendations from optimization-api (port 3005)
 * Shows real data from AWS cost analysis
 */

import React, { useState, useEffect, useMemo } from 'react';
import { withFreshData, authHeaders as sharedAuthHeaders } from '../services/apiHeaders';
import axios from 'axios';
import {
  RefreshCw,
  Eye,
  X,
  Clock,
  FlaskConical,
  Loader2,
  DollarSign,
  ListChecks,
  AlertTriangle,
  Layers,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { attachAuthRefresh } from '../services/apiClient';
import { ProviderTiles } from '../components/common/ProviderTiles';
import { RecommendationFilters } from '../components/recommendations/RecommendationFilters';
import { useNavigate } from 'react-router-dom';
import { adaptApiRecommendation } from '../utils/recommendationAdapter';
import { RecommendationFilters as ClientFilters } from '../types/recommendation';
import { Button } from '../components/common/Button';
import { IconButton } from '../components/common/IconButton';
import { Badge, BadgeVariant } from '../components/common/Badge';
import { StatCard } from '../components/common/StatCard';
import {
  StateBadge,
  StateGroup,
  StateGroupChips,
  RejectDialog,
  SnoozeDialog,
  allowedActions,
  runStateAction,
  useStateCatalogue,
} from '../components/recommendations/RecommendationLifecycle';

// Temporarily hidden for the demo, per explicit request - not needed there
// and adds visual clutter. Flip back to true to restore bulk select/action.
const BULK_ACTIONS_ENABLED = false;


// Requirement 36.3 - real source_system values from native-recommender-fetch.service.ts, mapped to a human-readable label.
export const NATIVE_ADVISOR_LABELS: Record<string, string> = {
  aws_compute_optimizer: 'AWS Compute Optimizer',
  aws_trusted_advisor: 'AWS Trusted Advisor',
  azure_advisor: 'Azure Advisor',
  gcp_recommender: 'GCP Recommender',
};

export const getTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    idle_resource: 'Idle Resource',
    rightsizing: 'Rightsizing',
    orphaned_resource: 'Orphaned Resource',
    old_snapshot: 'Old Snapshot',
    architecture_migration: 'Architecture Migration',
    licensing_optimization: 'Licensing Optimization',
    nonprod_premium_waste: 'Non-Prod Premium Waste',
    scheduled_use: 'Scheduled Use',
    // Was missing, so this one type rendered as the raw 'commitment_optimization'
    // string in the table while every other row showed a formatted label.
    commitment_optimization: 'Commitment Optimization',
    storage_optimization: 'Storage & Logs',
    modernization: 'Modernization',
    network_optimization: 'Network & Data Transfer',
    nonprod_optimization: 'Non-Prod Optimization',
    ai_optimization: 'AI / GenAI Spend',
    pricing_optimization: 'Pricing & Licensing',
  };
  return labels[type] || type;
};

export const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    idle_resource: 'bg-orange-500/10 text-orange-600',
    rightsizing: 'bg-sky-500/10 text-sky-600',
    orphaned_resource: 'bg-sky-500/10 text-sky-600',
    old_snapshot: 'bg-sky-500/10 text-sky-600',
    architecture_migration: 'bg-sky-500/10 text-sky-600',
    licensing_optimization: 'bg-sky-500/10 text-sky-600',
    nonprod_premium_waste: 'bg-amber-500/10 text-amber-600',
    scheduled_use: 'bg-sky-500/10 text-sky-600',
    commitment_optimization: 'bg-emerald-500/10 text-emerald-600',
    storage_optimization: 'bg-sky-500/10 text-sky-600',
    modernization: 'bg-sky-500/10 text-sky-600',
    network_optimization: 'bg-sky-500/10 text-sky-600',
    nonprod_optimization: 'bg-amber-500/10 text-amber-600',
    ai_optimization: 'bg-sky-500/10 text-sky-600',
    pricing_optimization: 'bg-emerald-500/10 text-emerald-600',
  };
  return colors[type] || 'bg-secondary text-foreground';
};

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';

// Recovers from an expired access token automatically (401 -> refresh -> retry
// once) instead of surfacing a permanent error the user could only clear by
// reloading the whole page - same behavior apiClient already gives other pages.
const optimizationApi = attachAuthRefresh(axios.create({ baseURL: OPTIMIZATION_API_URL }));

// optimization-api now requires a verified Bearer token on every request
// (previously had no auth at all - a real gap fixed alongside this).
// This was also a separate, duplicate copy of recommendationsService.ts's
// authHeaders() that never got the x-tenant-id fix applied there - it was
// still sending zero company context, so the backend's per-tenant gate
// always fell back to Demo regardless of which company was selected.
function authHeaders(): Record<string, string> {
  return sharedAuthHeaders();
}

export interface Recommendation {
  id: string;
  type: string;
  resource_id: string;
  resource_name?: string;
  account_id?: string;
  region?: string;
  provider: string;
  service: string;
  current_cost: number;
  potential_savings: number;
  reason: string;
  recommendation: string;
  confidence: string;
  priority: string;
  metadata?: Record<string, any>;
  created_at: string;
  current_cost_formatted?: string;
  potential_savings_formatted?: string;
  /** Lifecycle state - one of the 12 in GET /recommendations/states. */
  status?: string;
  saved_monthly?: number | null;
  saved_at?: string | null;
  reject_reason?: string | null;
  status_note?: string | null;
  snoozed_until?: string | null;
  status_changed_by?: string | null;
  status_changed_at?: string | null;
}

interface LifecycleBuckets {
  identified: { count: number; savings: number };
  in_progress: { count: number; savings: number };
  saved: { count: number; saved_monthly: number };
  set_aside: { count: number; savings: number };
}

interface RecommendationSummary {
  total_savings: number;
  total_savings_formatted?: string;
  recommendations_count: number;
  /** Findings carrying real money (potential_savings > 0). */
  actionable_count?: number;
  /** Zero-savings cleanup/governance findings. */
  hygiene_count?: number;
  by_category: Record<string, { count: number; savings: number }>;
  by_provider: Record<string, { count: number; savings: number }>;
  by_priority: Record<string, { count: number; savings: number }>;
  /** Phase 4.0 money buckets: Identified / In progress / Saved / Set aside. */
  lifecycle?: LifecycleBuckets;
}

const ALL_GROUPS: StateGroup[] = ['to_review', 'in_flight', 'needs_attention', 'saved', 'set_aside'];

const RecommendationsPage: React.FC = () => {
  const navigate = useNavigate();
  const costOptimizationEnabled = useFeatureFlag('cost_optimization_enabled');
  const catalogue = useStateCatalogue();
  // Gap 21: which states the list shows. `null` = the default view (To
  // review + In flight + Needs attention); a group chip or one exact state
  // from the dropdown narrows it. Sent to the server as ?status=, which
  // accepts group names and state values alike.
  const [stateGroup, setStateGroup] = useState<StateGroup | 'all' | null>(null);
  const [stateValue, setStateValue] = useState<string>('');
  const [dialog, setDialog] = useState<{ kind: 'reject' | 'snooze'; id: string } | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [summary, setSummary] = useState<RecommendationSummary | null>(null);
  // AC-WF-010 "orphaned-ticket dashboard" - the backend reconciliation
  // (ticket closed with no real evidence the resource was fixed -> reopened
  // automatically) has existed with zero frontend visibility until now.
  // Fetched once, independent of the main list's filters/pagination, since
  // this is a small always-relevant summary, not a filtered view.
  const [orphanedTickets, setOrphanedTickets] = useState<Array<{ id: string; resource_id: string; type: string; provider: string; potential_savings: number; reopened_at: string }>>([]);
  const [showOrphanedTickets, setShowOrphanedTickets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when SOME detectors failed - the list rendered is real but partial.
  // Distinct from `error`, which means the request itself failed.
  const [degraded, setDegraded] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [actioningId, setActioningId] = useState<string | null>(null);
  // Req 78 AC2: the table row is the "Level 1 executive" view (savings +
  // confidence + priority); clicking "View" opens the "Level 2 engineer"
  // detail (RecommendationDetailModal) - reason_code, detection method,
  // annualized savings, and superseded-by status, all of which the API
  // already returns in `metadata`. Now navigates to a dedicated route
  // (/recommendations/:id, RecommendationDetailPage) rather than opening a
  // modal - that page owns its own investigate/execute logic against a
  // single recommendation.
  // Requirement 78 AC1/AC2/AC4: category/cloud/risk-tier/status filtering and
  // the L1/L2/L3 detail modal, both real components that shipped in this
  // frontend's own source but were never imported by this page - wired in
  // as an additive client-side layer on top of the existing server-side
  // type/provider filters (kept as-is, they work and are cheaper - only one
  // API round-trip instead of re-fetching on every checkbox toggle).
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [clientFilters, setClientFilters] = useState<ClientFilters>({});
  const [ticketMessage, setTicketMessage] = useState<string | null>(null);
  // Real server-side pagination (Phase 1) - status filtering moved server-
  // side too (see fetchData's `status` param), so `clientFilters.statuses`
  // now drives a re-fetch instead of a local re-filter of an already-fully-
  // fetched list.
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // FR-WF-005 (Phase 13) - bulk Investigate/Reject, aggregating each row's
  // own real outcome (FR-AUTO-022 PARTIAL_SUCCESS) rather than one pass/
  // fail for the whole batch. Bulk CONFIRM (real execution) is deliberately
  // not offered here - see the backend route's own comment on why.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResultMessage, setBulkResultMessage] = useState<string | null>(null);
  const [delegateInput, setDelegateInput] = useState('');
  // Transient overlay for the approve -> confirm flow's in-flight/staged-
  // reveal states used to live here as `approvalPhase` - removed along with
  // the modal, since investigate/execute now run on RecommendationDetailPage,
  // which tracks its own phase state locally against one recommendation.

  // clientFilters.statuses used to only drive the local visibleRecommendations
  // useMemo - now it's a real server-side filter (see fetchData's `status`
  // param), so a change here has to trigger a re-fetch, not just a re-filter
  // of whatever page happened to already be in memory. Single effect keyed
  // on [page, statusFilterKey] - handleClientFiltersChange below resets page
  // to 1 in the SAME synchronous handler as the filter change (React 18
  // batches both into one render), so this fires exactly once per real
  // change instead of racing two separate effects against each other.
  // The ?status= value: one exact state, a group, every group, or nothing
  // (server default view).
  const statusParam = stateValue || (stateGroup === 'all' ? ALL_GROUPS.join(',') : stateGroup || '');
  const statusFilterKey = statusParam;
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilterKey]);

  // Type / priority / provider now refetch as soon as they change, which is
  // what removed the "Apply Filters" button: a select that visibly holds a new
  // value while the table below still shows the old results is its own small
  // bug, and every other filter on this page (status, provider tiles) already
  // applied immediately - so the button only governed two of them.
  //
  // Always resets to page 1: staying on page 4 of the OLD result set while
  // fetching a differently-sized new one is how you land on an empty table.
  const isFirstFilterRun = React.useRef(true);
  useEffect(() => {
    // Skip the mount pass; the effect above already fetched.
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setPage(1);
    fetchData(false, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, priorityFilter, providerFilter]);

  // AC-WF-010 orphaned-ticket dashboard - fetched once on mount, independent
  // of the main list's filters, since a stale ticket closure is relevant
  // regardless of what the user happens to be filtering the main table by.
  useEffect(() => {
    optimizationApi
      .get('/api/v1/recommendations/orphaned-tickets', { headers: authHeaders() })
      .then((res) => setOrphanedTickets(res.data?.data || []))
      .catch((err) => console.warn('Could not load orphaned tickets:', err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State filtering moved to the chips + dropdown above the table (Phase
  // 4.0); RecommendationFilters no longer carries statuses.
  const handleClientFiltersChange = (next: ClientFilters) => {
    setClientFilters(next);
  };

  const handleStateGroupChange = (next: StateGroup | 'all' | null) => {
    setStateGroup(next);
    setStateValue('');
    setPage(1);
  };

  const handleStateValueChange = (next: string) => {
    setStateValue(next);
    setPage(1);
  };

  // pageOverride lets a caller that's ALSO resetting page via setPage(1) in
  // the same handler (e.g. handleFilterChange) request page 1 immediately,
  // instead of racing the async setPage(1) commit and fetching the OLD page
  // number with the NEW filters for one request.
  const fetchData = async (isRefresh = false, pageOverride?: number) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (providerFilter !== 'all') params.append('provider', providerFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (statusParam) params.append('status', statusParam);
      params.append('page', String(pageOverride ?? page));
      params.append('pageSize', String(pageSize));

      const summaryParams = new URLSearchParams();
      if (providerFilter !== 'all') summaryParams.append('provider', providerFilter);
      if (typeFilter !== 'all') summaryParams.append('type', typeFilter);
      if (priorityFilter !== 'all') summaryParams.append('priority', priorityFilter);

      const [recResponse, summaryResponse] = await Promise.all([
        optimizationApi.get(`/api/v1/recommendations?${params.toString()}`, { headers: authHeaders() }),
        optimizationApi.get(`/api/v1/recommendations/summary?${summaryParams.toString()}`, { headers: authHeaders() }),
      ]);

      const freshRecommendations: Recommendation[] = recResponse.data.data || [];
      setRecommendations(freshRecommendations);
      setSummary(summaryResponse.data.data || null);
      setTotalCount(recResponse.data.meta?.total ?? freshRecommendations.length);
      setTotalPages(recResponse.data.meta?.totalPages ?? 1);

      // Partial detector failure: the list is genuine but incomplete. Without
      // this the page shows a short list with no hint that some detectors
      // never reported, which reads as "we checked everything and this is all
      // there is".
      const failedCount = recResponse.data.meta?.detectorsFailed ?? 0;
      const ranCount = recResponse.data.meta?.detectorsRun ?? 0;
      setDegraded(
        failedCount > 0
          ? `${failedCount} of ${ranCount} detectors failed on this run, so this list is incomplete. Retry, or check the optimization-api logs.`
          : null
      );
      return freshRecommendations;
    } catch (err: any) {
      // Prefer the API's own explanation over axios's "Request failed with
      // status code 503", which tells the user nothing. A 503 here means
      // detection is down, not that there is nothing to recommend.
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      setError(apiMessage || err.message || 'Failed to fetch recommendations');
      console.error('Error fetching recommendations:', err.response?.data ?? err);
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // handleFilterChange removed with the "Apply Filters" button - the
  // filter-change effect above now does exactly this (reset to page 1, refetch
  // immediately) whenever a filter value actually changes.

  const handleRefresh = () => {
    // withFreshData adds Cache-Control: no-cache so this re-queries source
    // instead of re-rendering the server-cached payload.
    withFreshData(() => fetchData(true));
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === visibleRecommendations.length ? new Set() : new Set(visibleRecommendations.map((r) => r.id))
    );
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    if (selectedIds.size === 0) return;
    setBulkRunning(true);
    setBulkResultMessage(null);
    try {
      const response = await optimizationApi.post(
        '/api/v1/recommendations/bulk-action',
        { ids: Array.from(selectedIds), action, delegatedTo: delegateInput || undefined },
        { headers: { ...authHeaders(), 'Idempotency-Key': `bulk-${action}-${Date.now()}-${Math.random().toString(36).slice(2)}` } }
      );
      const { summary, outcome } = response.data?.data || {};
      setBulkResultMessage(
        outcome === 'success'
          ? `${summary?.succeeded ?? 0} of ${summary?.total ?? 0} succeeded.`
          : `${summary?.succeeded ?? 0} of ${summary?.total ?? 0} succeeded, ${summary?.failed ?? 0} failed - see individual rows for details.`
      );
      setSelectedIds(new Set());
      setDelegateInput('');
      fetchData();
    } catch (err: any) {
      setBulkResultMessage(err.response?.data?.message || err.message || 'Bulk action failed');
    } finally {
      setBulkRunning(false);
    }
  };

  // 'approve' (Investigate) and 'confirm-action' (Execute/Open ticket) no
  // longer run from this page at all - both now live on
  // RecommendationDetailPage (/recommendations/:id), which owns its own copy
  // of this same logic against a single recommendation instead of the whole
  // list. This function only ever needs the two actions the row itself still
  // offers.
  // Reject and Snooze open a dialog (reason / date are required - gaps 9,
  // 10); the dialog posts the action and calls back here. The user is taken
  // from the login on the server, so no x-user-id header is sent (gap 18).
  const handleStateChanged = (id: string, action: 'reject' | 'snooze' | 'reopen') => {
    setDialog(null);
    const changedRec = recommendations.find((r) => r.id === id);
    // Requirement 32.3: setting aside today's top-ranked action for a
    // resource can promote the next-ranked one server-side (real DB-status-
    // aware re-ranking in recommendation.service.ts) - refetch quietly and
    // surface it if so, instead of leaving it undiscovered until a refresh.
    fetchData(true).then((freshList) => {
      if (action !== 'reject' || !changedRec) return;
      const promoted = freshList?.find(
        (r) => r.resource_id === changedRec.resource_id && r.id !== id && !r.metadata?.is_superseded
      );
      if (promoted) {
        setTicketMessage(
          `Alternative recommendation now available: ${getTypeLabel(promoted.type)} on ${promoted.resource_name || promoted.resource_id}`
        );
      }
    });
  };

  const handleReopen = async (id: string) => {
    setActioningId(id);
    try {
      await runStateAction(id, 'reopen');
      handleStateChanged(id, 'reopen');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reopen recommendation');
    } finally {
      setActioningId(null);
    }
  };

  // handleConfirmAction removed from this page entirely - Execute/Open
  // ticket now only runs from RecommendationDetailPage, which has its own
  // copy of this same call against a single recommendation rather than the
  // whole list.

  // Client-side layer for the category/cloud/risk-tier/min-savings/search
  // filters RecommendationFilters exposes - applied on top of the already-
  // fetched, server-filtered+paginated page. `statuses` moved to a real
  // server-side filter (see fetchData's `status` param + handleClientFiltersChange)
  // since filtering a single already-fetched page client-side can never
  // recover rows a status filter should surface from OTHER pages.
  const visibleRecommendations = useMemo(() => {
    if (
      !clientFilters.categories?.length &&
      !clientFilters.cloudProviders?.length &&
      !clientFilters.riskTiers?.length &&
      clientFilters.minSavings === undefined &&
      !clientFilters.searchQuery
    ) {
      return recommendations;
    }
    return recommendations.filter((rec) => {
      const ui = adaptApiRecommendation(rec as any);
      if (clientFilters.categories?.length && !clientFilters.categories.includes(ui.category)) return false;
      if (clientFilters.cloudProviders?.length && !clientFilters.cloudProviders.includes(ui.cloudProvider)) return false;
      if (clientFilters.riskTiers?.length && !clientFilters.riskTiers.includes(ui.riskTier)) return false;
      if (clientFilters.minSavings !== undefined && ui.estimatedMonthlySavings < clientFilters.minSavings) return false;
      if (clientFilters.searchQuery && !ui.description.toLowerCase().includes(clientFilters.searchQuery.toLowerCase()) && !ui.title.toLowerCase().includes(clientFilters.searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [recommendations, clientFilters]);

  const getPriorityBadgeVariant = (priority: string): BadgeVariant => {
    const variants: Record<string, BadgeVariant> = {
      high: 'danger',
      medium: 'warning',
      low: 'success',
    };
    return variants[priority] || 'muted';
  };

  if (costOptimizationEnabled === false) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center">
        <h2 className="text-sm font-semibold text-foreground">Cost Optimization is turned off</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This feature is disabled for your account. Turn it on in Admin Console → Feature Flags.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary/30 mx-auto"></div>
          {/* Reflects the ACTUAL scope being queried. This was hardcoded to
              "across AWS, Azure, and GCP" even when a single provider tile was
              selected, so filtering to AWS still claimed to be scanning three
              clouds - which misrepresents what the request is doing and makes
              a provider-filtered reload look like it ignored the filter. */}
          <p className="mt-4 text-muted-foreground">
            {providerFilter === 'all'
              ? 'Loading recommendations across AWS, Azure, and GCP...'
              : `Loading ${providerFilter.toUpperCase()} recommendations...`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end gap-2">
        <Button onClick={handleRefresh} disabled={refreshing} size="sm">
          {refreshing ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* AC-WF-010 orphaned-ticket dashboard - the backend reconciliation
          this surfaces (ticket closed with no real evidence the resource
          was actually fixed -> reopened automatically) had no frontend at
          all before this; the audit list per row above shows a small
          "Reopened" badge, this is the aggregate view the spec calls a
          "dashboard". */}

      {/* Provider tiles - same treatment as Cost Explorer/Dashboard */}
      {/* Provider selector - shared component so Cost Explorer,
          Recommendations, Anomalies and the Dashboard cannot drift apart. */}
      <ProviderTiles value={providerFilter} onChange={setProviderFilter} />

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Some detectors failed: the list below is real but incomplete. Shown
          as a warning rather than an error because what did load is usable. */}
      {!error && degraded && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-lg text-sm">
          {degraded}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Phase 4.0 money buckets. "Identified" is savings found and not
              yet acted on; "In progress" is being fixed; "Saved" is only what
              the bill has PROVEN (Verified + Resolved, measured, never more
              than the estimate). Three different things, never added up. */}
          <StatCard
            label="Identified Savings"
            value={`$${(summary.lifecycle?.identified.savings ?? summary.total_savings).toFixed(2)}`}
            subtext={`per month · ${summary.lifecycle?.identified.count ?? summary.actionable_count ?? summary.recommendations_count} to review`}
            icon={DollarSign}
            accent="primary"
          />
          <StatCard
            label="In Progress"
            value={`$${(summary.lifecycle?.in_progress.savings ?? 0).toFixed(2)}`}
            subtext={`per month · ${summary.lifecycle?.in_progress.count ?? 0} being fixed`}
            icon={ListChecks}
            accent="primary"
          />
          <StatCard
            label="Saved (proven on the bill)"
            value={`$${(summary.lifecycle?.saved.saved_monthly ?? 0).toFixed(2)}`}
            subtext={`per month · ${summary.lifecycle?.saved.count ?? 0} verified or resolved`}
            icon={Layers}
            accent="success"
          />
          <StatCard
            label="High Priority"
            value={summary.by_priority?.high?.count || 0}
            subtext={`$${(summary.by_priority?.high?.savings || 0).toFixed(2)} savings`}
            icon={AlertTriangle}
            accent="danger"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-lg border border-border shadow-sm p-3 dark:border-border dark:bg-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full h-9 px-3 py-0 text-sm leading-none border border-border rounded-md dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="all">All Types</option>
              <option value="idle_resource">Idle Resources</option>
              <option value="rightsizing">Rightsizing</option>
              <option value="orphaned_resource">Orphaned Resources</option>
              <option value="old_snapshot">Old Snapshots</option>
              <option value="architecture_migration">Architecture Migration</option>
              <option value="licensing_optimization">Licensing Optimization</option>
              <option value="nonprod_premium_waste">Non-Prod Premium Waste</option>
              <option value="scheduled_use">Scheduled Use</option>
              <option value="commitment_optimization">Commitment Optimization</option>
              <option value="storage_optimization">Storage &amp; Logs</option>
              <option value="modernization">Modernization</option>
              <option value="network_optimization">Network &amp; Data Transfer</option>
              <option value="nonprod_optimization">Non-Prod Optimization</option>
              <option value="ai_optimization">AI / GenAI Spend</option>
              <option value="pricing_optimization">Pricing &amp; Licensing</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Priority</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full h-9 px-3 py-0 text-sm leading-none border border-border rounded-md dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* "Apply Filters" removed - the selects above now refetch on
              change (see the effect near the top of this component). It was
              also misleading: status and the provider tiles never went through
              it, so it only ever governed two of the five filters. */}
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMoreFilters((v) => !v)}
              aria-expanded={showMoreFilters}
            >
              <SlidersHorizontal />
              {showMoreFilters ? 'Fewer' : 'More'} filters
            </Button>
          </div>
        </div>
      </div>

      {/* Category/cloud/risk-tier/status/search filters - client-side, layered on
          top of the server-side type/provider filters above */}
      {showMoreFilters && (
        <RecommendationFilters filters={clientFilters} onFiltersChange={handleClientFiltersChange} />
      )}

      {/* Gap 21: state groups + an exact-state dropdown, both from the
          server's state catalogue. "Active" = the default view. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StateGroupChips catalogue={catalogue} value={stateValue ? null : stateGroup} onChange={handleStateGroupChange} />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          State
          <select
            value={stateValue}
            onChange={(e) => handleStateValueChange(e.target.value)}
            className="h-8 rounded-md border border-border bg-card px-2 text-sm text-foreground"
            aria-label="Filter by exact state"
          >
            <option value="">Any in view</option>
            {catalogue.groups.map((g) => (
              <optgroup key={g.value} label={g.label}>
                {catalogue.states.filter((s) => s.group === g.value).map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      </div>

      {ticketMessage && (
        <div className="bg-primary/10 border border-primary/30 text-primary px-4 py-2.5 rounded-lg flex justify-between items-center text-sm">
          <span>{ticketMessage}</span>
          <IconButton onClick={() => setTicketMessage(null)} aria-label="Dismiss">
            <X />
          </IconButton>
        </div>
      )}

      {BULK_ACTIONS_ENABLED && selectedIds.size > 0 && (
        <div className="bg-gray-800 text-white px-4 py-2.5 rounded-lg flex flex-wrap items-center gap-2.5">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <input
            type="text"
            value={delegateInput}
            onChange={(e) => setDelegateInput(e.target.value)}
            placeholder="Delegate to (optional email)"
            className="h-8 px-2 rounded text-foreground text-sm"
          />
          {/* Same icon as the per-row Investigate action - this triggers the
              identical dry-run/preview step, just for every selected row. */}
          <Button onClick={() => handleBulkAction('approve')} disabled={bulkRunning} size="sm" variant="success">
            <FlaskConical />
            Investigate Selected
          </Button>
          <Button onClick={() => handleBulkAction('reject')} disabled={bulkRunning} size="sm" variant="danger">
            Reject Selected
          </Button>
          <button onClick={() => setSelectedIds(new Set())} className="text-muted-foreground/70 hover:text-white text-sm ml-auto">
            Clear selection
          </button>
        </div>
      )}

      {bulkResultMessage && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-lg flex justify-between items-center text-sm">
          <span>{bulkResultMessage}</span>
          <IconButton onClick={() => setBulkResultMessage(null)} aria-label="Dismiss">
            <X />
          </IconButton>
        </div>
      )}

      {/* Recommendations Table - table-fixed with explicit column widths so
          no column can force horizontal scroll; the long "Recommendation"
          text and Provider/Service columns were the main width offenders,
          now merged into Resource/Cost cells or moved into the detail modal. */}
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden dark:border-border dark:bg-card">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-border text-xs">
            {/* Percentage widths, not fixed px (w-48 etc.) - table-fixed +
                w-full proportionally STRETCHES fixed-px column widths to
                fill the real container width, so a column given more raw
                width than its content needs (Actions: 4 small icons/one
                button) ends up with a large empty strip on the right once
                stretched - confirmed live. Percentages that already sum to
                100% aren't stretched the same way. */}
            <colgroup>
              {BULK_ACTIONS_ENABLED && <col className="w-8" />}
              {/* Sums to 100%. Percentages, not fixed px: under `table-fixed
                  w-full` the browser proportionally STRETCHES declared pixel
                  widths to fill the real container, which is what produced the
                  phantom gap in Actions in earlier attempts.

                  Resource 32->27 and Actions 19->13, because neither was using
                  its allocation - the longest real resource value fills about
                  70% of the old Resource column, and Actions held three 32px
                  icon buttons (~104px) in roughly 200px. The freed width went
                  to Type, whose labels were truncating, and to Status. */}
              <col className="w-[9%]" />
              <col className="w-[19%]" />
              <col className="w-[27%]" />
              <col className="w-[15%]" />
              <col className="w-[17%]" />
              <col className="w-[13%]" />
            </colgroup>
            <thead className="bg-muted/40">
              <tr>
                {BULK_ACTIONS_ENABLED && (
                  <th className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label="Select all visible recommendations"
                      checked={visibleRecommendations.length > 0 && selectedIds.size === visibleRecommendations.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground uppercase">Priority</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground uppercase">Type</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground uppercase">Resource</th>
                <th className="px-3 py-2.5 text-right font-medium text-muted-foreground uppercase">Cost</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {visibleRecommendations.length === 0 ? (
                <tr>
                  <td colSpan={BULK_ACTIONS_ENABLED ? 7 : 6} className="px-6 py-12 text-center text-sm text-muted-foreground">
                    No recommendations found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                visibleRecommendations.map((rec) => {
                  const isSuperseded = Boolean(rec.metadata?.is_superseded);
                  return (
                  <tr key={rec.id} className={`hover:bg-muted/30 dark:hover:bg-secondary/40 ${isSuperseded ? 'opacity-60' : ''}`}>
                    {BULK_ACTIONS_ENABLED && (
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          aria-label={`Select recommendation for ${rec.resource_id}`}
                          checked={selectedIds.has(rec.id)}
                          onChange={() => toggleSelected(rec.id)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Badge variant={getPriorityBadgeVariant(rec.priority)}>{rec.priority.toUpperCase()}</Badge>
                    </td>
                    <td className="max-w-0 overflow-hidden px-3 py-2.5">
                      <span className={`inline-block max-w-full truncate align-bottom px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(rec.type)}`} title={getTypeLabel(rec.type)}>
                        {getTypeLabel(rec.type)}
                      </span>
                    </td>
                    <td className="max-w-0 overflow-hidden px-3 py-2.5">
                      {/* Full resource ID/ARN is already on the detail view
                          (click "View" or here) - the row itself only needs
                          enough to identify what kind of resource this is. */}
                      <button
                        onClick={() => navigate(`/recommendations/${rec.id}`)}
                        className="block w-full max-w-full truncate text-left text-foreground hover:text-primary-700 hover:underline dark:text-foreground dark:hover:text-primary-400"
                        title={rec.resource_id}
                      >
                        {rec.provider.toUpperCase()} · {rec.service}
                      </button>
                      {/* Always rendered (even blank) so every row reserves the
                          same second line - a row with no account_id yet
                          shouldn't be visibly shorter than its neighbors,
                          which read as broken/uneven table alignment. */}
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground" title={rec.account_id ? `Account ${rec.account_id}` : undefined}>
                        {rec.account_id ? `Acct ${rec.account_id}` : ' '}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {/* A native-advisor finding (AWS Compute Optimizer et al)
                          carries a real SAVING but no current spend - the
                          backend flags that with current_cost_known:false
                          rather than passing off its 0 placeholder as a
                          measurement. Showing "$0.00" beside "save $16.01"
                          read as "costs nothing, save $16", so show "-". */}
                      <div className="text-foreground">
                        {rec.metadata?.current_cost_known === false
                          ? <span className="text-muted-foreground" title="Reported by the cloud provider's own advisor, which does not include the resource's current spend">&mdash;</span>
                          : (rec.current_cost_formatted || `$${rec.current_cost.toFixed(2)}`)}
                      </div>
                      {/* A zero-savings finding is not a $0 saving - it is a
                          different KIND of finding. Free-to-delete objects
                          (launch templates, idle ASGs) and rightsizing UPSIZE
                          warnings (which cost more, not less) both land here.
                          Rendering them as "save $0.00" in savings-green reads
                          as a broken calculation; naming them does not. */}
                      {rec.potential_savings > 0 ? (
                        <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                          save {rec.potential_savings_formatted || `$${rec.potential_savings.toFixed(2)}`}
                        </div>
                      ) : (
                        <div className="text-xs font-medium text-muted-foreground" title="Cleanup or risk-avoidance finding - no direct cost saving">
                          no cost impact
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {/* Fixed width + centered so every status pill in the
                          column lines up the same regardless of label length
                          ("PENDING" vs "IN PROGRESS") - a content-sized pill
                          reads as broken alignment when several sit stacked
                          in one column. */}
                      <StateBadge rec={rec} catalogue={catalogue} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {/* Every action beyond these three quick ones
                          (Investigate, Execute/Open ticket) now lives inside
                          the detail panel's Investigation tab - the row's own
                          Status column already shows in_progress/pending/
                          completed, so a second, redundant busy-text/Done
                          state here was never adding information, just
                          visual noise and inconsistent row widths. */}
                      <div className="flex items-center gap-2">
                        <IconButton onClick={() => navigate(`/recommendations/${rec.id}`)} title="View details" aria-label="View details">
                          <Eye />
                        </IconButton>
                        {/* Only the moves allowed from this row's state
                            (the server re-checks state and role). */}
                        {allowedActions(catalogue, rec.status).some((a) => a.action === 'snooze') && (
                          <IconButton
                            onClick={() => setDialog({ kind: 'snooze', id: rec.id })}
                            disabled={actioningId === rec.id}
                            title="Snooze until a date"
                            aria-label="Snooze"
                          >
                            <Clock />
                          </IconButton>
                        )}
                        {allowedActions(catalogue, rec.status).some((a) => a.action === 'reject') && (
                          <IconButton
                            onClick={() => setDialog({ kind: 'reject', id: rec.id })}
                            disabled={actioningId === rec.id}
                            title="Reject (reason required)"
                            aria-label="Reject"
                          >
                            <X />
                          </IconButton>
                        )}
                        {allowedActions(catalogue, rec.status).some((a) => a.action === 'reopen') && (
                          <IconButton
                            onClick={() => handleReopen(rec.id)}
                            disabled={actioningId === rec.id}
                            title="Reopen"
                            aria-label="Reopen"
                          >
                            <RotateCcw />
                          </IconButton>
                        )}
                        {/* Raise Ticket deliberately NOT in the row.
                            It lives on the detail page instead: raising a ticket
                            is a commitment to do work, and it should follow
                            actually reading the evidence (Metrics / Changes /
                            Related), not be a one-click action from a list you
                            are still skimming. It also kept the row from fitting
                            without wrapping. */}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-border">
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages} &middot; {totalCount} recommendation{totalCount === 1 ? '' : 's'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {dialog?.kind === 'reject' && (
        <RejectDialog catalogue={catalogue} recId={dialog.id} onClose={() => setDialog(null)} onDone={() => handleStateChanged(dialog.id, 'reject')} />
      )}
      {dialog?.kind === 'snooze' && (
        <SnoozeDialog catalogue={catalogue} recId={dialog.id} onClose={() => setDialog(null)} onDone={() => handleStateChanged(dialog.id, 'snooze')} />
      )}

      {/* Auto-recovered tickets. Moved to the FOOT of the page: this is a
          reassuring audit note ("we noticed a ticket was closed without
          evidence and reopened it"), not something needing action, and
          leading with it made the page open on a warning. The query behind
          it is now bounded to 30 days too - it was unbounded, so one
          historical reopen kept this visible forever. */}
      {orphanedTickets.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 dark:border-sky-800 dark:bg-sky-900/20">
          <button
            type="button"
            onClick={() => setShowOrphanedTickets((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
          >
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-800 dark:text-sky-300">
              <RotateCcw className="h-3.5 w-3.5 shrink-0" />
              {orphanedTickets.length} ticket{orphanedTickets.length === 1 ? '' : 's'} auto-recovered - closed without evidence the fix actually worked, so we reopened{orphanedTickets.length === 1 ? ' it' : ' them'} for you
            </span>
            <span className="shrink-0 text-xs text-sky-700 dark:text-sky-400">{showOrphanedTickets ? 'Hide' : 'Show'}</span>
          </button>
          {showOrphanedTickets && (
            <div className="px-4 pb-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-sky-700 dark:text-sky-400 uppercase">
                    <th className="text-left py-1 pr-4">Resource</th>
                    <th className="text-left py-1 pr-4">Type</th>
                    <th className="text-left py-1 pr-4">Provider</th>
                    <th className="text-left py-1 pr-4">Savings</th>
                    <th className="text-left py-1">Reopened</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanedTickets.map((t) => (
                    <tr key={t.id} className="border-t border-sky-100 dark:border-sky-800/50">
                      <td className="py-1 pr-4 text-foreground">{t.resource_id}</td>
                      <td className="py-1 pr-4 text-muted-foreground">{getTypeLabel(t.type)}</td>
                      <td className="py-1 pr-4 text-muted-foreground uppercase">{t.provider}</td>
                      <td className="py-1 pr-4 text-muted-foreground">${Number(t.potential_savings || 0).toFixed(2)}</td>
                      <td className="py-1 text-muted-foreground dark:text-muted-foreground">{new Date(t.reopened_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { RecommendationsPage };
